import { Component, computed, inject, input, OnInit, output, signal, WritableSignal } from '@angular/core';
import { IUserGroup } from '@core/interfaces/user-group.interface';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { CampaignDetailsDeliverablesFilterSelectComponent } from '@pages/campaigns/campaign-details/campaign-details-deliverables/campaign-details-deliverables-filter-select/campaign-details-deliverables-filter-select.component';
import { ICampaignDetailsDeliverablesFilterOption } from '@pages/campaigns/campaign-details/campaign-details-deliverables/campaign-details-deliverables.interface';
import { ProfileDateFilterComponent } from '@shared/components/profile-date-filter/profile-date-filter.component';
import {
  EDateRangeType,
  ICompareRange,
  IDateRangeBounds,
  IProfileDateFilterState,
} from '@shared/components/profile-date-filter/profile-date-filter.interface';
import { endOfDay, startOfDay, subDays, subYears } from 'date-fns';
import { IEventAnalyticsScopeState } from '../admin-event-analytics.interface';
import { IDateRange } from './admin-event-analytics-filters.interface';

const FILTER_TRANSLATION_KEY_PREFIX = 'pages.admin.eventAnalytics.filter';
/** How far back "Full period" reaches - there's no real entity to bound it by (unlike a single campaign). */
const ALL_TIME_YEARS_BACK = 5;

@Component({
  imports: [CampaignDetailsDeliverablesFilterSelectComponent, ProfileDateFilterComponent, TranslateModule],
  selector: 'app-admin-event-analytics-filters',
  templateUrl: './admin-event-analytics-filters.component.html',
})
export class AdminEventAnalyticsFiltersComponent implements OnInit {
  public readonly userGroups = input<IUserGroup[] | null>(null);
  public readonly userGroupsLoading = input<boolean>(false);

  public readonly scopeChanged = output<IEventAnalyticsScopeState>();

  public readonly filterTranslationKeyPrefix = FILTER_TRANSLATION_KEY_PREFIX;
  public readonly excludedRangeTypes: EDateRangeType[] = [EDateRangeType.thisWeek, EDateRangeType.thisMonth];

  public readonly allTimeBounds: IDateRangeBounds;
  public readonly initialDateFilterState: IProfileDateFilterState;

  public readonly userGroupOptions = computed<ICampaignDetailsDeliverablesFilterOption<string>[]>(() =>
    (this.userGroups() ?? []).map((userGroup) => ({ label: userGroup.name, value: userGroup.id })),
  );

  public readonly userGroupEmptyLabel = computed<string>(() =>
    this.translate.instant(`${FILTER_TRANSLATION_KEY_PREFIX}.allGroups`, {
      count: (this.userGroups() ?? []).length,
    }),
  );

  private readonly translate = inject(TranslateService);

  public readonly selectedUserGroupIds = signal<Set<string>>(new Set());

  private readonly startDate: WritableSignal<Date | null>;
  private readonly endDate: WritableSignal<Date | null>;
  private readonly compareRange = signal<ICompareRange | null>(null);

  constructor() {
    const now = endOfDay(new Date());
    const allTimeStart = startOfDay(subYears(now, ALL_TIME_YEARS_BACK));
    // Default to the last 7 days (inclusive) rather than the full period - mirrors
    // `ProfileDateFilterComponent`'s own `calculatePredefinedRange(EDateRangeType.last7Days)`.
    const defaultStart = startOfDay(subDays(now, 6));

    this.allTimeBounds = { end: now, start: allTimeStart };
    this.initialDateFilterState = { endDate: now, selectedRange: EDateRangeType.last7Days, startDate: defaultStart };
    this.startDate = signal<Date | null>(defaultStart);
    this.endDate = signal<Date | null>(now);
  }

  public ngOnInit(): void {
    // Emitted here rather than the constructor - Angular only wires up this component's output
    // listener on the parent once construction finishes, so emitting any earlier would be lost.
    this.emitScope();
  }

  public onDateRangeChanged(range: IDateRange): void {
    this.startDate.set(range.startDate);
    this.endDate.set(range.endDate);
    this.emitScope();
  }

  public onCompareRangeChanged(range: ICompareRange | null): void {
    this.compareRange.set(range);
    this.emitScope();
  }

  public onUserGroupSelectionChanged(userGroupIds: Set<string>): void {
    this.selectedUserGroupIds.set(userGroupIds);
  }

  public onUserGroupFilterPanelClosed(): void {
    this.emitScope();
  }

  private emitScope(): void {
    this.scopeChanged.emit({
      compareRange: this.compareRange(),
      endDate: this.endDate(),
      selectedUserGroupIds: this.selectedUserGroupIds(),
      startDate: this.startDate(),
    });
  }
}
